import Icon, {icons} from './icon'
import PropTypes from 'prop-types'
import React, {Component, Fragment} from 'react'
import cloneDeep from 'lodash/cloneDeep'
import isEqual from 'lodash/isEqual'
import {IndexContext} from './tracking'
import {css} from 'glamor'

import {colors} from '../styles'

const GRID_CONTAINER_WIDTH = 992
const GRID_CONTAINER_GRID_SIZE = 9
const GRID_IMAGE_SCALE = 104.5
const LIST_IMAGE_SCALE = 90
const IMAGE_RATIO = 1.4
const SWIPE_SIZE = 30
const DOT_SIZE = 20

const style = {
  arrow: css({
    background: 'none',
    border: 'none',
    margin: 0,
    padding: 0,
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    '& .fa': {
      color: colors.gray46
    }
  }),
  arrowLeft: css({
    left: 0
  }),
  arrowRight: css({
    right: 0
  }),
  button: css({
    border: '1px solid transparent',
    margin: 0,
    padding: 0,
    '&:focus': {
      outline: 'none',
      borderColor: colors.primaryBG
    }
  }),
  carousel: css({
    listStyle: 'none',
    overflowX: 'hidden',
    overflowY: 'hidden',
    padding: 0,
    margin: 0,
    scrollBehavior: 'smooth',
    whiteSpace: 'nowrap',
    '@media (min-width: 992px)': {
      height: '372px'
    },
    '@media (max-width: 991px)': {
      margin: '0 -15px',
      padding: '0 15px'
    }
  }),
  carouselGrid: css({
    width: '457px'
  }),
  carouselListItem: css({
    display: 'inline-block',
    verticalAlign: 'top'
  }),
  carouselPane: css({
    padding: 0,
    whiteSpace: 'normal',
    width: '457px'
  }),
  design: css({
    background: colors.white,
    border: `5px solid ${colors.white}`,
    display: 'inline-block'
  }),
  designActive: css({
    border: `2px solid ${colors.defaultText}`,
    margin: '5px',
    '> button': {
      margin: '4px',
      overflow: 'hidden',
      '> span > img': {
        margin: '-6px'
      }
    },
    '> button:focus': {
      borderColor: 'transparent'
    }
  }),
  navigation: css({
    listStyle: 'none',
    margin: '15px 0',
    padding: 0,
    position: 'relative',
    textAlign: 'center',
    '> ul': {
      margin: '0 15px',
      padding: 0,
      overflow: 'hidden',
      scrollBehavior: 'smooth',
      whiteSpace: 'nowrap'
    }
  }),
  navigationDot: css({
    background: colors.lightBG,
    border: 'none',
    borderRadius: '5px',
    display: 'inline-block',
    height: '10px',
    margin: '0 5px',
    padding: 0,
    width: '10px',
    '&:focus': {
      boxShadow: `0 0 4px ${colors.primaryBG}`,
      outline: 'none'
    }
  }),
  navigationDotActive: css({
    background: colors.primaryBG,
    '&:focus': {
      boxShadow: 'none'
    }
  })
}

const DesignPane = ({
  designs,
  indexModifier,
  onChange,
  renderer,
  scale,
  selectedDesign
}) => (
  <Fragment>
    {designs.map((design, index) => {
      let designStyle = style.design

      if (design.id === selectedDesign) {
        designStyle = css(designStyle, style.designActive)
      }

      design.scale = scale

      return (
        <IndexContext.Provider key={design.id} value={index}>
          <li {...designStyle}>
            <button
              className="designButton"
              onClick={() =>
                onChange(
                  design,
                  index + indexModifier * GRID_CONTAINER_GRID_SIZE
                )
              }
              {...style.button}
            >
              {renderer(design, index)}
            </button>
          </li>
        </IndexContext.Provider>
      )
    })}
  </Fragment>
)

DesignPane.propTypes = {
  designs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired
    })
  ).isRequired,
  indexModifier: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  renderer: PropTypes.func.isRequired,
  scale: PropTypes.number.isRequired,
  selectedDesign: PropTypes.number.isRequired
}

DesignPane.defaultProps = {
  indexModifier: 0
}

// TODO(Josh): convert to Swipeable component
export default class DesignCarousel extends Component {
  constructor(props) {
    super(props)

    this.state = {
      currentDot: 0,
      dotCount: 3,
      shouldDisplayGrid: false
    }

    this.dotRef = React.createRef()
  }

  componentDidMount() {
    window.addEventListener('resize', this.calculateDotCount)

    this.carouselRef.addEventListener('mousedown', this.startSwipe)
    this.carouselRef.addEventListener('touchstart', this.startSwipe)

    this.carouselRef.addEventListener('mousemove', this.watchSwipe)
    this.carouselRef.addEventListener('touchmove', this.watchSwipe)

    this.carouselRef.addEventListener('mouseup', this.endSwipe)
    this.carouselRef.addEventListener('touchend', this.endSwipe)

    this.calculateDotCount()
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!isEqual(this.props.designs, nextProps.designs)) {
      this.calculateDotCount(nextProps)
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.calculateDotCount)
  }

  startSwipe = (event) => {
    this.startX = (event.touches ? event.touches[0] : event).clientX
  }

  watchSwipe = (event) => {
    if (!this.startX) {
      return
    }

    this.endX = (event.touches ? event.touches[0] : event).clientX
  }

  endSwipe = (event) => {
    const isLeftSwipe = this.endX - this.startX > SWIPE_SIZE
    const isRightSwipe = this.startX - this.endX > SWIPE_SIZE

    delete this.startX
    delete this.endX

    if (isLeftSwipe) {
      event.preventDefault()

      this.scrollLeft()
    } else if (isRightSwipe) {
      event.preventDefault()

      this.scrollRight()
    }
  }

  onChange = (design, index) => {
    this.props.onChange(design, index)

    const isLeftOfPage =
      index < this.state.currentDot * this.state.productImagesPerWindow
    const isRightOfPage =
      index >= (this.state.currentDot + 1) * this.state.productImagesPerWindow

    if (isLeftOfPage) {
      this.scrollLeft()
    } else if (isRightOfPage) {
      this.scrollRight()
    }
  }

  calculateDotCount = (props) => {
    const {designs} = !props || props instanceof Event ? this.props : props
    const shouldDisplayGrid = window.innerWidth >= GRID_CONTAINER_WIDTH
    const productImageWidth =
      Math.round(
        (shouldDisplayGrid ? GRID_IMAGE_SCALE : LIST_IMAGE_SCALE) * IMAGE_RATIO
      ) + 6

    const productImagesPerWindow = shouldDisplayGrid
      ? GRID_CONTAINER_GRID_SIZE
      : Math.floor(this.carouselRef.clientWidth / productImageWidth)

    // This can happen if the window resize event fires before the carouselRef
    // is in the DOM.
    if (!productImagesPerWindow) {
      return
    }

    const dotCount = Math.ceil(designs.length / productImagesPerWindow)
    let currentDotDivisor = shouldDisplayGrid
      ? (productImagesPerWindow * productImageWidth) / 3
      : productImagesPerWindow * productImageWidth

    const currentDot = Math.floor(
      this.carouselRef.scrollLeft / currentDotDivisor
    )

    this.setState(
      {
        currentDot,
        dotCount,
        productImagesPerWindow,
        shouldDisplayGrid
      },
      () => this.scrollToCurrentDot()
    )
  }

  setCarouselRef = (element) => {
    this.carouselRef = element
  }

  scrollLeft = () => {
    this.setState(
      (state) => ({
        currentDot: Math.max(state.currentDot - 1, 0)
      }),
      () => this.scrollToCurrentDot()
    )
  }

  scrollRight = () => {
    this.setState(
      (state) => ({
        currentDot: Math.min(state.currentDot + 1, state.dotCount - 1)
      }),
      () => this.scrollToCurrentDot()
    )
  }

  scrollToCurrentDot = () => {
    const productImageWidth =
      Math.round(
        (this.state.shouldDisplayGrid ? GRID_IMAGE_SCALE : LIST_IMAGE_SCALE) *
          IMAGE_RATIO
      ) + 6

    if (this.state.shouldDisplayGrid) {
      this.carouselRef.scrollLeft =
        3 * this.state.currentDot * productImageWidth
    } else {
      this.carouselRef.scrollLeft =
        this.state.productImagesPerWindow *
        this.state.currentDot *
        productImageWidth
    }

    const {el} = this.dotRef.current
    if (el) {
      const dotsPerScreen = Math.floor(el.clientWidth / DOT_SIZE)
      const dotOffset = el.scrollLeft / DOT_SIZE

      if (dotsPerScreen < this.state.dotCount) {
        if (this.state.currentDot >= dotsPerScreen + dotOffset - 1) {
          el.scrollTo(el.scrollLeft + 20, 0)
        } else if (this.state.currentDot <= dotOffset + 1) {
          el.scrollTo(el.scrollLeft - 20, 0)
        }
      }
    }
  }

  scrollToDot = (event) => {
    event.preventDefault()

    this.setState(
      {
        currentDot: Number(event.target.value)
      },
      () => this.scrollToCurrentDot()
    )
  }

  render() {
    const {children, designs, selectedDesign} = this.props

    let navStyle
    let designPanes

    if (this.state.shouldDisplayGrid) {
      navStyle = style.carouselGrid

      designPanes = []
      let paneDesigns = cloneDeep(designs)

      while (paneDesigns.length !== 0) {
        designPanes.push(paneDesigns.splice(0, 9))
      }
    }

    return (
      <nav {...navStyle}>
        <ul {...style.carousel} ref={this.setCarouselRef}>
          {designPanes ? (
            designPanes.map((pane, index) => (
              <IndexContext.Provider
                key={pane.map((design) => design.id).join('-')}
                value={index}
              >
                <li {...style.carouselListItem}>
                  <ul {...style.carouselPane}>
                    <DesignPane
                      designs={pane}
                      indexModifier={index}
                      onChange={this.onChange}
                      renderer={children}
                      scale={104.5}
                      selectedDesign={selectedDesign}
                    />
                  </ul>
                </li>
              </IndexContext.Provider>
            ))
          ) : (
            <DesignPane
              designs={designs}
              onChange={this.onChange}
              renderer={children}
              scale={90}
              selectedDesign={selectedDesign}
            />
          )}
        </ul>
        <div {...style.navigation}>
          <button
            onClick={this.scrollLeft}
            {...css(style.arrow, style.arrowLeft)}
          >
            <Icon icon={icons.chevronLeft} size="lg" />
          </button>
          <ul ref={this.dotRef}>
            {new Array(Math.max(1, this.state.dotCount))
              .fill(1)
              .map((dot, index) => {
                let dotStyle = style.navigationDot

                if (index === this.state.currentDot) {
                  dotStyle = css(dotStyle, style.navigationDotActive)
                }

                return (
                  <button
                    onClick={this.scrollToDot}
                    key={index}
                    value={index}
                    {...dotStyle}
                  />
                )
              })}
          </ul>
          <button
            onClick={this.scrollRight}
            {...css(style.arrow, style.arrowRight)}
          >
            <Icon icon={icons.chevronRight} size="lg" />
          </button>
        </div>
      </nav>
    )
  }
}

DesignCarousel.propTypes = {
  children: PropTypes.func.isRequired,
  designs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number
    })
  ).isRequired,
  name: PropTypes.string,
  onChange: PropTypes.func,
  selectedDesign: PropTypes.number
}
